/**
 * Wizard "Collega unità locale e XFIR RENTRI" — .
 */
import { useState, type KeyboardEvent } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { xfirStorageKey } from "@/lib/xfirEnabled";

const STEPS = [
  "Accedi al portale RENTRI con il certificato configurato in Impostazioni.",
  "Verifica che il certificato di firma sia presente e valido.",
  "Seleziona l'operatore registrato da abilitare.",
  "Sul portale RENTRI, apri il menu operatore (tasto destro sul nome).",
  "Carica l'immagine del QR code (.bmp) generato dal portale.",
  "Copia il codice dal portale (icona occhio) e incollalo qui sotto.",
];

export function XfirEnableWizard({
  open,
  onOpenChange,
  numIscrSito,
  onEnabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numIscrSito: string;
  onEnabled?: () => void;
}) {
  const [step, setStep] = useState(0);
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [portalCode, setPortalCode] = useState("");

  const reset = () => {
    setStep(0);
    setQrFile(null);
    setPortalCode("");
  };

  const handleEnable = () => {
    const code = portalCode.trim();
    const devMockQr =
      import.meta.env.DEV && code.localeCompare("MOCKXFIR_DEMO", undefined, { sensitivity: "accent" }) === 0;

    if (!qrFile && !devMockQr) {
      toast.error("Carica l'immagine del QR code");
      return;
    }
    if (!code && !devMockQr) {
      toast.error("Incolla il codice dal portale RENTRI");
      return;
    }

    const effectiveQrName = qrFile?.name ?? (devMockQr ? "demo-mock.bmp" : "");

    localStorage.setItem(xfirStorageKey(numIscrSito), "1");
    localStorage.setItem(
      `${xfirStorageKey(numIscrSito)}.meta`,
      JSON.stringify({
        enabled_at: new Date().toISOString(),
        qr_name: effectiveQrName,
        code_len: (devMockQr ? "MOCKXFIR_DEMO" : portalCode).length,
        ...(devMockQr ? { dev_mock: true } : {}),
      }),
    );
    toast.success("Creazione formulari digitali abilitata per questa unità locale");
    onEnabled?.();
    onOpenChange(false);
    reset();
  };

  const wizardKeyNavigation = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey && e.key === "PageDown") {
      if (step < STEPS.length - 1) {
        e.preventDefault();
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
      }
      return;
    }
    if (e.ctrlKey && e.key === "PageUp") {
      e.preventDefault();
      setStep((s) => Math.max(0, s - 1));
      return;
    }
    if (e.ctrlKey && e.key === "Enter" && step === STEPS.length - 1) {
      e.preventDefault();
      handleEnable();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        tabIndex={-1}
        onKeyDownCapture={wizardKeyNavigation}
        className="top-[72px] flex max-h-[calc(100dvh-96px)] min-h-0 max-w-lg translate-y-0 flex-col gap-0 overflow-hidden border p-0 sm:top-[72px] sm:translate-y-0"
      >
        <div className="flex max-h-[calc(100dvh-96px)] min-h-0 flex-col">
          <DialogHeader className="shrink-0 space-y-1.5 px-6 pb-2 pt-6">
            <DialogTitle>Collega unità locale e XFIR RENTRI</DialogTitle>
            <DialogDescription className="sr-only">
              Procedura guidata per collegare l&apos;unità locale selezionata al servizio XFIR RENTRI.
            </DialogDescription>
            <p className="text-xs text-muted-foreground font-mono">{numIscrSito}</p>
          </DialogHeader>

          <div className="relative min-h-0 flex-1 overflow-y-auto">
            <div className="px-6 pb-2">
              <ol className="space-y-3 text-sm">
                {STEPS.map((text, i) => (
                  <li
                    key={i}
                    className={
                      i === step
                        ? "rounded-md border border-primary/40 bg-primary/5 p-2"
                        : "text-muted-foreground"
                    }
                  >
                    <span className="font-medium text-foreground">{i + 1}. </span>
                    {text}
                  </li>
                ))}
              </ol>

              {step >= 4 && (
                <div className="mt-4 space-y-3 border-t pt-3">
                  <label className="block text-xs font-medium">Carica immagine QR code (.bmp)</label>
                  <Input
                    type="file"
                    accept=".bmp,image/bmp,image/*"
                    onChange={(e) => setQrFile(e.target.files?.[0] ?? null)}
                  />
                  <label className="block text-xs font-medium">Codice dal portale RENTRI</label>
                  <Input
                    value={portalCode}
                    onChange={(e) => setPortalCode(e.target.value)}
                    placeholder="Incolla il codice…"
                    className="font-mono"
                  />
                </div>
              )}
            </div>

            <div
              className="sticky bottom-0 z-20 mt-6 flex w-full flex-col gap-2 border-t bg-background px-6 py-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.35)]"
              role="group"
              aria-label="Azioni procedura XFIR"
            >
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
              >
                Annulla
              </Button>
              {step < STEPS.length - 1 ? (
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => setStep((s) => Math.min(s + 1, STEPS.length - 1))}
                >
                  Avanti
                </Button>
              ) : (
                <Button type="button" className="w-full" onClick={handleEnable}>
                  Abilita creazione formulari digitali
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
